import React, { Children, Component } from 'react';

import _get from 'lodash.get';
import _set from 'lodash.set';

import { HyperMediaAPI, HyperMediaType } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { ContainerProvider } from '@philips-emr/tws-core/lib/component';
import { arrayOf, bool, element, list, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { enumValues, isEqualShallow, isObject, propagateData } from '@philips-emr/tws-utils';

const { LIST, OBJECT } = HyperMediaType;

const acceptedTypes = enumValues(ComponentType);

@AwesomeComponent({ type: ComponentType.BEHAVIOR, api: HyperMediaAPI })
class HyperMedia extends Component {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: object.notEnumerable(),
    loading: bool,
    resources: list,
    type: oneOf(enumValues(HyperMediaType)),
    uuid: string,
  };

  static defaultProps = {
    type: OBJECT,
    resources: [],
    loading: false,
  };

  state = {};

  updateData() {
    const { type } = this.props;

    if (type === LIST) {
      this.mergeEmbedded();
    } else {
      this.fetchLinks();
    }
  }

  fetchLinks = () => {
    const { data, resources } = this.props;

    if (!isObject(data)) return;

    const resolveEndpoint = (resource) => {
      const lastDot = resource.lastIndexOf('.');
      const path = resource.substr(0, lastDot + 1);
      const name = resource.substr(lastDot + 1);
      const endpoint = _get(data, `${path}_links.${name}.href`);

      return { path, name, endpoint };
    };

    const fetchResource = (resObject) => {
      if (!resObject.endpoint) {
        return resObject;
      }

      return HttpService.get(resObject.endpoint)
        .then(({ body }) => ({ ...resObject, data: body }))
        .catch(() => {});
    };

    const requests = resources
      .map(resolveEndpoint)
      .map(fetchResource);

    this.setState({ resourcesData: data, loadingResource: true });

    Promise.all(requests).then((responses) => {
      const resObjects = responses.filter(isObject);

      const resourcesData = resObjects.reduce((result, { path, name, data }) => (
        _set(result, `${path}${name}`, data)
      ), { ...data });

      this.setState({ resourcesData, loadingResource: false });
    });
  };

  mergeEmbedded() {
    const { data, resources } = this.props;

    if (!isObject(data) || !data._embedded) return;

    const resourcesData = resources
      .map(id => data._embedded[id])
      .filter(Boolean)
      .flat();

    this.setState({ resourcesData });
  }

  componentDidMount() {
    this.updateData();
  }

  componentDidUpdate(prevProps) {
    if (isEqualShallow(prevProps, this.props)) {
      return;
    }

    this.updateData();
  }

  render() {
    const { resourcesData, loadingResource } = this.state;
    const { children, uuid, loading } = this.props;

    if (Children.count(children) === 0) {
      const Container = ContainerProvider.provide(this);
      return <Container acceptedTypes={acceptedTypes} className="u-expand" uuid={uuid} />;
    }

    return propagateData(children, resourcesData, { loading: loading || loadingResource });
  }
}

export default HyperMedia;
